import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { AnnouncedDateParam, LastUpdatedParam, SeverityParam, UpstreamEvent, listUpstreamEventsQueue } from 'client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { LoaderFunctionArgs, redirect, useLoaderData, useNavigation } from 'react-router';
import { SearchInput } from 'routes/card-optimizer/components/search-input';
import {
  TableLayoutActions,
  TableLayoutBody,
  TableLayoutFilterPills,
  TableLayoutHeader,
  TableLayoutHeaderRow,
  TableLayoutRoot,
} from 'routes/card-optimizer/components/table-layout';
import { FilterPills } from 'routes/components/filter-pills';
import { DisruptionMonitorTabs } from 'routes/disruption-monitor/components/disruption-monitor-tabs';

import { createBreadcrumbConfig } from 'DesignSystem/features/dynamic-breadcrumbs/dynamic-breadcrumbs.utils';
import { Table } from 'DesignSystem/features/table-context/table';
import { TableProvider } from 'DesignSystem/features/table-context/table-provider';
import { LogErrorBoundary } from 'components/Errors/LogErrorBoundary';
import { TableMetaFilter } from 'types/react-table';
import { queryClient } from 'utils/api';
import {
  clearUiSettingQueryCache,
  createUiSettingSyncParamsAdapter,
  hydrateSyncParamsUiSetting,
  scheduleUiSettingPut,
  uiSettingQueryKey,
} from 'utils/api/uiSettingQueries';
import { getFlag } from 'utils/launchDarklyClient';
import { syncRouteParamsV2 } from 'utils/sync-route-params/sync-route-params-v2';

import {
  DEFAULT_DISRUPTION_EVENT_STATUS_FILTERS_V2_SYNC_PARAMS,
  disruptionEventStatusFiltersV2Schema,
} from '../sync-route-params-schemas';
import { getEventQueueColumns } from './components/columns';
import FilterDrawer from './components/filter-drawer';
import { AnnouncedDateFilter, AnnouncedDateFilterPill } from './components/filters/announced-date-select';
import { CustomerStatusFilter, CustomerStatusFilterPill } from './components/filters/customer-status-select';
import { EventTypeFilter, EventTypeFilterPill } from './components/filters/event-type-select';
import { HasAffectedItemsToggle, HasAffectedItemsTogglePill } from './components/filters/has-affected-items-toggle';
import {
  HasImpactedLocationsToggle,
  HasImpactedLocationsTogglePill,
} from './components/filters/has-impacted-locations-toggle';
import { LastUpdatedFilter, LastUpdatedFilterPill } from './components/filters/last-updated-select';
import { SeverityFilter, SeverityFilterPill } from './components/filters/severity-select';
import { StatusFilter, StatusFilterPill } from './components/filters/status-select';
import EventQueuePagination from './components/pagination';
import {
  DEFAULT_EVENT_QUEUE_SORT_DIRECTION,
  DEFAULT_EVENT_QUEUE_SORT_FIELD,
  eventQueueStatusParamsNeedRedirect,
  normalizeCustomerStatusList,
  resolveEventQueueStatusForUrl,
} from './utils';

const tableMetaFilters = [
  {
    param: 'has_affected_items',
    label: 'Has Affected Items',
    components: {
      Filter: HasAffectedItemsToggle,
      Pill: HasAffectedItemsTogglePill,
    },
  },
  {
    param: 'has_impacted_locations',
    label: 'Has Impacted Locations',
    components: {
      Filter: HasImpactedLocationsToggle,
      Pill: HasImpactedLocationsTogglePill,
    },
  },
  {
    param: 'status',
    label: 'Status',
    components: {
      Filter: StatusFilter,
      Pill: StatusFilterPill,
    },
  },
  {
    param: 'customer_status',
    label: 'Your Status',
    components: {
      Filter: CustomerStatusFilter,
      Pill: CustomerStatusFilterPill,
    },
  },
  {
    param: 'severity',
    label: 'Severity',
    components: {
      Filter: SeverityFilter,
      Pill: SeverityFilterPill,
    },
  },
  {
    param: 'event_type',
    label: 'Event Type',
    components: {
      Filter: EventTypeFilter,
      Pill: EventTypeFilterPill,
    },
  },
  {
    param: 'announced_date',
    label: 'Announced Date',
    components: {
      Filter: AnnouncedDateFilter,
      Pill: AnnouncedDateFilterPill,
    },
  },
  {
    param: 'last_updated',
    label: 'Last Updated',
    components: {
      Filter: LastUpdatedFilter,
      Pill: LastUpdatedFilterPill,
    },
  },
] satisfies Array<TableMetaFilter<UpstreamEvent>>;

export function getEventQueueFilters(eventMitigationProject: boolean) {
  if (eventMitigationProject) return tableMetaFilters;
  return tableMetaFilters.filter((filter) => filter.param !== 'customer_status');
}

const EVENT_STATUS_FILTERS_SETTING = 'disruption_event_status_filters_v2';

function redirectIfEventStatusParamsNeedUpdate(url: URL, statuses: string[]) {
  const rawStatus = url.searchParams.getAll('status');
  if (!eventQueueStatusParamsNeedRedirect(rawStatus, statuses)) return null;

  const next = new URL(url);
  next.searchParams.delete('status');
  for (const status of statuses) {
    next.searchParams.append('status', status);
  }
  return redirect(`${next.pathname}${next.search}${next.hash}`);
}

export async function loader({ request }: LoaderFunctionArgs) {
  const hydrated = await hydrateSyncParamsUiSetting(
    EVENT_STATUS_FILTERS_SETTING,
    DEFAULT_DISRUPTION_EVENT_STATUS_FILTERS_V2_SYNC_PARAMS
  );
  // Seed default status into cache so `_breadcrumb=reset` restores Announced/Ongoing
  // even when stored settings only have pagination (or are empty).
  queryClient.setQueryData(uiSettingQueryKey(EVENT_STATUS_FILTERS_SETTING), {
    ...DEFAULT_DISRUPTION_EVENT_STATUS_FILTERS_V2_SYNC_PARAMS,
    ...hydrated,
  });

  await syncRouteParamsV2(request, {
    storageKey: EVENT_STATUS_FILTERS_SETTING,
    schema: disruptionEventStatusFiltersV2Schema,
    storage: createUiSettingSyncParamsAdapter(EVENT_STATUS_FILTERS_SETTING),
  });

  const url = new URL(request.url);
  const searchParams = url.searchParams;

  const severityParamsStrings = searchParams.getAll('severity');
  const severityParams = severityParamsStrings.map((param) => parseInt(param) as SeverityParam);
  const statusParams = resolveEventQueueStatusForUrl(searchParams.getAll('status'));
  const statusRedirect = redirectIfEventStatusParamsNeedUpdate(url, statusParams);
  if (statusRedirect) throw statusRedirect;

  const eventMitigationProject = await getFlag('eventMitigationProject');
  const customerStatusParams = eventMitigationProject
    ? normalizeCustomerStatusList(searchParams.getAll('customer_status'))
    : [];
  const requestedSort = searchParams.get('sort');
  const sort =
    requestedSort && (eventMitigationProject || !requestedSort.startsWith('customer_status|'))
      ? [requestedSort]
      : [`${DEFAULT_EVENT_QUEUE_SORT_FIELD}|${DEFAULT_EVENT_QUEUE_SORT_DIRECTION}`];

  const response = await listUpstreamEventsQueue({
    query: {
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
      page_size: searchParams.get('page_size') ? parseInt(searchParams.get('page_size')!) : 25,
      search: searchParams.get('search') ?? undefined,
      has_affected_items: searchParams.get('has_affected_items')
        ? searchParams.get('has_affected_items') === 'true'
        : undefined,
      has_impacted_locations: searchParams.get('has_impacted_locations')
        ? searchParams.get('has_impacted_locations') === 'true'
        : undefined,
      status: statusParams,
      ...(customerStatusParams.length > 0 ? { customer_status: customerStatusParams } : {}),
      severity: searchParams.getAll('severity') ? severityParams : undefined,
      event_type: searchParams.getAll('event_type') ? (searchParams.getAll('event_type') as Array<string>) : undefined,
      announced_date: searchParams.get('announced_date')
        ? (parseInt(searchParams.get('announced_date')!) as AnnouncedDateParam)
        : undefined,
      last_updated: searchParams.get('last_updated')
        ? (parseInt(searchParams.get('last_updated')!) as LastUpdatedParam)
        : undefined,
      sort,
    },
  });
  const data = response.data?.events ?? [];
  const count = response.data?.result_set_size ?? 0;

  return {
    data: data as UpstreamEvent[],
    count,
    ...createBreadcrumbConfig({
      label: 'Events',
      module: 'DISRUPTION_MONITOR',
    }),
  };
}

export function useEventsQuery() {
  const loaderData = useLoaderData<typeof loader>();
  return loaderData;
}

export function Component() {
  const navigation = useNavigation();
  const isFetching = navigation.state === 'loading';
  const data = useEventsQuery().data;
  const { eventMitigationProject } = useFlags();
  const columns = React.useMemo(() => getEventQueueColumns(Boolean(eventMitigationProject)), [eventMitigationProject]);
  const filters = React.useMemo(() => getEventQueueFilters(Boolean(eventMitigationProject)), [eventMitigationProject]);

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    autoResetPageIndex: false,
    enableSortingRemoval: false,
    meta: {
      filters,
      defaultFilters: DEFAULT_DISRUPTION_EVENT_STATUS_FILTERS_V2_SYNC_PARAMS,
    },
  });

  const handleResetFilters = React.useCallback(() => {
    clearUiSettingQueryCache(EVENT_STATUS_FILTERS_SETTING);
    scheduleUiSettingPut(EVENT_STATUS_FILTERS_SETTING, DEFAULT_DISRUPTION_EVENT_STATUS_FILTERS_V2_SYNC_PARAMS, 0);
  }, []);

  return (
    <div className="flex h-[--app-viewport-height] min-h-full w-full flex-col overflow-y-hidden bg-background">
      <header className="border-b bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 px-8 pb-4 pt-6">
          <DisruptionMonitorTabs />
        </div>
      </header>
      <div className="relative mx-auto flex min-h-0 w-full flex-col p-2 pb-1 md:p-8 md:py-2">
        <TableLayoutRoot>
          <TableProvider table={table} isLoading={isFetching}>
            <TableLayoutHeader>
              <TableLayoutHeaderRow>
                <div>
                  <SearchInput name="search" placeholder="Search by Event Description" className="h-8" />
                </div>
                <TableLayoutActions>
                  <FilterDrawer />
                </TableLayoutActions>
              </TableLayoutHeaderRow>
              <TableLayoutHeaderRow>
                <TableLayoutFilterPills>
                  <FilterPills onReset={handleResetFilters} />
                </TableLayoutFilterPills>
              </TableLayoutHeaderRow>
            </TableLayoutHeader>
            <TableLayoutBody>
              <Table containerClassName="h-full" />
            </TableLayoutBody>
          </TableProvider>
        </TableLayoutRoot>
      </div>
      <div className="relative mx-auto w-full p-2 pt-0 md:px-8 md:pb-3 md:pt-0">
        <EventQueuePagination />
      </div>
    </div>
  );
}

Component.displayName = 'DisruptionMonitorEvents';

export const ErrorBoundary = () => <LogErrorBoundary routeComponent="DisruptionMonitorEvents" />;
